import { useCallback, useMemo, useState } from 'react';
import { BigNum, MarketType, SpotMarketConfig } from '@velocity-exchange/sdk';
import { useAllIfData } from 'src/hooks/query/ifData/useAllIfData';
import { DRIFT_TOKEN_BANK_INDEX } from 'src/environmentVariables/EnvironmentVariables';
import {
	MarketId,
	InsuranceFundOperationsMap,
} from '@velocity-exchange/common';
import useDisplayPrecisionForCollateral from 'src/hooks/useDisplayPrecisionForCollateral';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useAddInsuranceFundStake } from 'src/hooks/driftActions/insuranceFund/useAddInsuranceFundStake';
import { useCancelInsuranceFundUnstake } from 'src/hooks/driftActions/insuranceFund/useCancelInsuranceFundUnstake';
import { useWithdrawInsuranceFundAssets } from 'src/hooks/driftActions/insuranceFund/useWithdrawInsuranceFundAssets';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useUtilizationIsTooHighForIFUnstake from 'src/hooks/useUtilizationIsTooHighForIFUnstake';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';

interface Params {
	isDriftStaking: boolean | undefined;
	defaultSelectedMarket: SpotMarketConfig;
	onConfirm?: () => void;
}

export default function useStakingUtils({
	isDriftStaking,
	defaultSelectedMarket,
	onConfirm,
}: Params) {
	const isWalletConnected = useWalletIsConnected();
	const { data: ifStakeData } = useAllIfData();
	const { addInsuranceFundStake } = useAddInsuranceFundStake();
	const { cancelInsuranceFundUnstakeRequest } = useCancelInsuranceFundUnstake();
	const { withdrawInsuranceFundUnstakedAssets } =
		useWithdrawInsuranceFundAssets();
	const { captureEvent } = usePostHogCapture();
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [amount, setAmount] = useState('');
	const [selectedMarket, setSelectedMarket] = useState(defaultSelectedMarket);

	const amountBignum = BigNum.fromPrint(
		amount ? amount : '0',
		selectedMarket.precisionExp
	);

	const excludedMarkets = useMemo(() => {
		return Object.values(ifStakeData)
			.filter((ifData) => {
				if (isDriftStaking) {
					return ifData.marketIndex !== DRIFT_TOKEN_BANK_INDEX;
				}

				return ifData.pausedOperations.includes(
					InsuranceFundOperationsMap.INIT
				);
			})
			.map((vault) => vault.marketIndex);
	}, [ifStakeData, isDriftStaking]);

	const displayPrecision = useDisplayPrecisionForCollateral(
		selectedMarket.marketIndex
	);

	const selectedMarketId = useMemo(
		() => new MarketId(selectedMarket.marketIndex, MarketType.SPOT),
		[selectedMarket.marketIndex]
	);

	const unstakeUtilizationCutoffResult =
		useUtilizationIsTooHighForIFUnstake(selectedMarketId);

	const selectedMarketStakeData = ifStakeData?.[selectedMarket.marketIndex];

	const userStake = selectedMarketStakeData?.userStake;
	const existingUnstakeRequest = userStake?.unstakeRequest;
	const userStakeLoaded = userStake?.loaded;
	const userNeedsStakeAccount =
		isWalletConnected && userStakeLoaded && !userStake.account;
	const userStakeAccountBalance = userStake?.currentStake ?? 0;
	const userStakeCostBasis = userStake?.costBasis ?? 0;

	const stakeVaultNextApr = selectedMarketStakeData?.vault?.nextApr ?? 0;

	const unstakingPeriod =
		(selectedMarketStakeData?.vault?.account?.unstakingPeriod?.toNumber() ??
			0) * 1000;

	const unstakingPeriodAsDays = Math.floor(
		unstakingPeriod / 1000 / 60 / 60 / 24
	);

	const {
		hasExistingUnstakeRequest,
		existingUnstakeRequestAmount,
		existingUnstakeRequestTimestamp,
		canWithdraw,
	} = (() => {
		if (existingUnstakeRequest) {
			const existingUnstakeRequestTime =
				existingUnstakeRequest.timestamp + unstakingPeriod;

			return {
				hasExistingUnstakeRequest: true,
				existingUnstakeRequestAmount: existingUnstakeRequest.amount,
				existingUnstakeRequestTimestamp: new Date(existingUnstakeRequestTime),
				canWithdraw:
					!unstakeUtilizationCutoffResult.unstakingIsDisabled &&
					Date.now() >= existingUnstakeRequestTime,
			};
		}

		return {
			hasExistingUnstakeRequest: false,
			existingUnstakeRequestAmount: 0,
			existingUnstakeRequestTimestamp: new Date(0 + unstakingPeriod),
			canWithdraw: false,
		};
	})();

	const handleSelectedMarketChange = useCallback((market: SpotMarketConfig) => {
		setSelectedMarket(market);

		setAmount('');
	}, []);

	const handleCancelUnstakeRequest = useCallback(async () => {
		setIsSubmitting(true);

		const result = await cancelInsuranceFundUnstakeRequest({
			marketIndex: selectedMarket.marketIndex,
		});

		captureEvent('if_staking_unstake_canceled', {
			spot_market_symbol: selectedMarket.symbol,
		});

		setIsSubmitting(false);

		if (result === true) {
			onConfirm?.();
		}
	}, [
		cancelInsuranceFundUnstakeRequest,
		captureEvent,
		onConfirm,
		selectedMarket,
	]);

	const handleWithdrawUnstakedAssets = useCallback(async () => {
		setIsSubmitting(true);

		const result = await withdrawInsuranceFundUnstakedAssets({
			marketIndex: selectedMarket.marketIndex,
		});

		captureEvent('if_staking_unstaked_assets_withdrawn', {
			spot_market_symbol: selectedMarket.symbol,
		});

		setIsSubmitting(false);

		if (result === true) {
			onConfirm?.();
		}
	}, [
		withdrawInsuranceFundUnstakedAssets,
		selectedMarket,
		captureEvent,
		onConfirm,
	]);

	return {
		selectedMarket,
		selectedMarketStakeData,
		amount,
		setAmount,
		amountBignum,
		userNeedsStakeAccount,
		excludedMarkets,
		unstakingPeriod,
		unstakingPeriodAsDays,
		isWalletConnected,
		userStakeLoaded,
		userStakeAccountBalance,
		userStakeCostBasis,
		stakeVaultNextApr,
		hasExistingUnstakeRequest,
		selectedMarketId,
		unstakeUtilizationCutoffResult,
		existingUnstakeRequestAmount,
		existingUnstakeRequestTimestamp,
		canWithdraw,
		handleCancelUnstakeRequest,
		handleWithdrawUnstakedAssets,
		handleSelectedMarketChange,
		displayPrecision,
		addInsuranceFundStake,
		captureEvent,
		isGeoblocked,
		isSubmitting,
		setIsSubmitting,
	};
}
